import React from 'react';
import { bookmarksAPI } from '../utils/api.js';


const BookmarksContext = React.createContext({
  bookmarksCache: new Map(),
  unitBookmarksCache: new Map(),
  loadBookmarks: () => {},
  loadAllBookmarks: () => {},
  refreshAllBookmarks: () => {},
  isBookmarked: () => false,
  toggleBookmark: () => {},
  isUnitBookmarked: () => false,
  toggleUnitBookmark: () => {},
});


export function BookmarksProvider({ children }) {
  const [bookmarksCache, setBookmarksCache] = React.useState(new Map());
  const [unitBookmarksCache, setUnitBookmarksCache] = React.useState(new Map());
  const [loading, setLoading] = React.useState(false);

  const loadedAllRef = React.useRef(false);
  const loadingAllRef = React.useRef(false);

  function normalizeType(b) {
    return (
      b?.bookmark_type || b?.bookmarkType || b?.type || b?.sourceType ||
      (b?.ayah != null ? 'ayah' : null)
    );
  }

  function getUnitKey(type, value) {
    return `${type}:${value}`;
  }

  function normalizeId(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const id = obj.id ?? obj.bookmark_id ?? obj.bookmarkId;
    if (id == null) return obj;
    if (obj.id === id) return obj;
    return { ...obj, id };
  }

  async function fetchAllBookmarks() {
    if (loadingAllRef.current) return;
    loadingAllRef.current = true;
    setLoading(true);

    try {
      const all = [];
      const limit = 500;
      let offset = 0;
      for (let i = 0; i < 20; i++) {
        const page = await bookmarksAPI.getAll({ limit, offset });
        const arr = Array.isArray(page) ? page : [];
        all.push(...arr);
        if (arr.length < limit) break;
        offset += limit;
      }

      const ayahMap = new Map();
      const unitMap = new Map();
      for (const b of all) {
        const bb = normalizeId(b);
        const type = normalizeType(bb);
        if (!type) continue;

        if (type === 'ayah') {
          const s = Number(bb?.surah);
          const a = Number(bb?.ayah);
          if (!Number.isFinite(s) || !Number.isFinite(a)) continue;
          ayahMap.set(`${s}:${a}`, bb);
        } else if (type === 'surah') {
          const s = Number(bb?.surah);
          if (!Number.isFinite(s)) continue;
          unitMap.set(getUnitKey('surah', s), bb);
        } else if (type === 'juz') {
          const j = Number(bb?.juz);
          if (!Number.isFinite(j)) continue;
          unitMap.set(getUnitKey('juz', j), bb);
        } else if (type === 'hizb') {
          const h = Number(bb?.hizb);
          if (!Number.isFinite(h)) continue;
          unitMap.set(getUnitKey('hizb', h), bb);
        } else if (type === 'page') {
          const p = Number(bb?.page);
          if (!Number.isFinite(p)) continue;
          unitMap.set(getUnitKey('page', p), bb);
        }
      }

      setBookmarksCache(ayahMap);
      setUnitBookmarksCache(unitMap);
      loadedAllRef.current = true;
    } finally {
      loadingAllRef.current = false;
      setLoading(false);
    }
  }


  const loadBookmarks = React.useCallback(async (ayahs) => {
    if (loadedAllRef.current) return;
    if (!Array.isArray(ayahs) || ayahs.length === 0) return;
    try {
      await fetchAllBookmarks();
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  }, []);

  const loadAllBookmarks = React.useCallback(async () => {
    if (loadedAllRef.current) return;
    try {
      await fetchAllBookmarks();
    } catch (error) {
      console.error('Error loading all bookmarks:', error);
    }
  }, []);

  const refreshAllBookmarks = React.useCallback(async () => {
    try {
      await fetchAllBookmarks();
    } catch (error) {
      console.error('Error refreshing all bookmarks:', error);
    }
  }, []);


  const isBookmarked = React.useCallback((surah, ayah) => {
    const key = `${surah}:${ayah}`;
    return !!bookmarksCache.get(key);
  }, [bookmarksCache]);

  const isUnitBookmarked = React.useCallback((type, value) => {
    const key = getUnitKey(type, value);
    return !!unitBookmarksCache.get(key);
  }, [unitBookmarksCache]);


  const toggleBookmark = React.useCallback(async (surah, ayah, sourceType, sourceId) => {
    const key = `${surah}:${ayah}`;
    const current = bookmarksCache.get(key) || null;
    const currentState = !!current;

    try {
      if (currentState) {
        const id = current?.id;
        if (id != null) {
          await bookmarksAPI.deleteById(id);
        } else {
          await fetchAllBookmarks();
        }
      } else {
        const created = await bookmarksAPI.create({
          bookmark_type: 'ayah',
          surah: Number(surah),
          ayah: Number(ayah),
        });
        if (created && typeof created === 'object') {
          const id = created.id ?? created.bookmark_id ?? created.bookmarkId;
          const obj = id != null ? { ...created, id, bookmark_type: 'ayah', surah: Number(surah), ayah: Number(ayah) } : null;
          if (obj) {
            setBookmarksCache(prev => {
              const next = new Map(prev);
              next.set(key, obj);
              return next;
            });
            window.dispatchEvent(new Event('bookmarkChanged'));
            return true;
          }
        }
      }

      setBookmarksCache(prev => {
        const newCache = new Map(prev);
        if (currentState) newCache.delete(key);
        else newCache.set(key, { id: null, bookmark_type: 'ayah', surah: Number(surah), ayah: Number(ayah) });
        return newCache;
      });

      if (!loadedAllRef.current) loadedAllRef.current = true;

      if (!currentState) {
        try { await fetchAllBookmarks(); } catch {}
      }

      window.dispatchEvent(new Event('bookmarkChanged'));
      
      return !currentState;
    } catch (error) {
      console.error(`Error toggling bookmark ${surah}:${ayah}:`, error);
      throw error;
    }
  }, [bookmarksCache]);

  const toggleUnitBookmark = React.useCallback(async (type, value) => {
    const key = getUnitKey(type, value);
    const current = unitBookmarksCache.get(key) || null;
    const currentState = !!current;

    try {
      if (currentState) {
        const id = current?.id;
        if (id != null) {
          await bookmarksAPI.deleteById(id);
        } else {
          await fetchAllBookmarks();
        }
        setUnitBookmarksCache(prev => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
      } else {
        const payload = { bookmark_type: type };
        if (type === 'surah') payload.surah = Number(value);
        if (type === 'juz') payload.juz = Number(value);
        if (type === 'hizb') payload.hizb = Number(value);
        if (type === 'page') payload.page = Number(value);

        const created = await bookmarksAPI.create(payload);
        if (created && typeof created === 'object') {
          const id = created.id ?? created.bookmark_id ?? created.bookmarkId;
          const obj = id != null ? { ...created, id, ...payload } : { ...payload, id: null };
          setUnitBookmarksCache(prev => {
            const next = new Map(prev);
            next.set(key, obj);
            return next;
          });
        } else {
          await fetchAllBookmarks();
        }
      }

      window.dispatchEvent(new Event('bookmarkChanged'));
      return !currentState;
    } catch (error) {
      console.error(`Error toggling unit bookmark ${type}:${value}:`, error);
      throw error;
    }
  }, [unitBookmarksCache]);


  const clearCache = React.useCallback(() => {
    setBookmarksCache(new Map());
    setUnitBookmarksCache(new Map());
    loadedAllRef.current = false;
  }, []);

  const value = {
    bookmarksCache,
    unitBookmarksCache,
    loadBookmarks,
    loadAllBookmarks,
    refreshAllBookmarks,
    isBookmarked,
    toggleBookmark,
    isUnitBookmarked,
    toggleUnitBookmark,
    clearCache,
    loading,
  };

  return (
    <BookmarksContext.Provider value={value}>
      {children}
    </BookmarksContext.Provider>
  );
}


export function useBookmarks() {
  const context = React.useContext(BookmarksContext);
  if (!context) {
    throw new Error('useBookmarks must be used within BookmarksProvider');
  }
  return context;
}
